import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';

interface UserProfile {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  linkedInURL?: string;
  hasBeenPromptedForLinkedIn?: boolean;
  organization?: string;
  createdAt: Date;
  updatedAt: Date;
}

type AccessState = 'allowed' | 'pending_enable' | 'disabled' | 'unknown';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  accessState: AccessState;
  accessMessage: string;
  loading: boolean;
  isAdmin: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  retryAuth: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Session cache — stores auth state in sessionStorage so repeat page loads
// render immediately without a spinner.  Scoped per uid so multiple accounts
// on the same browser stay isolated.  Cleared on sign-out.
// ---------------------------------------------------------------------------

interface AuthCache {
  accessState: AccessState;
  isAdmin: boolean;
  profile: Omit<UserProfile, 'createdAt' | 'updatedAt'>;
  cachedAt: number;
}

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour — matches Firebase token lifetime

function cacheKey(uid: string) {
  return `tsja_auth_${uid}`;
}

function readCache(uid: string): AuthCache | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(uid));
    if (!raw) return null;
    const parsed: AuthCache = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(cacheKey(uid));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(uid: string, cache: AuthCache) {
  try {
    sessionStorage.setItem(cacheKey(uid), JSON.stringify(cache));
  } catch {
    // sessionStorage full or unavailable — not fatal
  }
}

function clearCache(uid: string) {
  try {
    sessionStorage.removeItem(cacheKey(uid));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [accessState, setAccessState] = useState<AccessState>('unknown');
  const [accessMessage, setAccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  /**
   * Call /api/auth/me — verifies token, checks disabled status, and returns
   * profile fields + is_admin in one round-trip.
   * Returns null on network error (caller decides whether to treat as fatal).
   */
  const checkBackendAccess = async (currentUser: User): Promise<{
    isAdmin: boolean;
    profile: Partial<UserProfile>;
    accessState: AccessState;
    accessMessage: string;
  } | null> => {
    const token = await getIdToken(currentUser, true);
    const url = `${process.env.REACT_APP_API_URL || ''}/api/auth/me`;
    let response: Response | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        break;
      } catch {
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
        else return null; // backend unreachable — caller uses cache
      }
    }
    if (!response) return null;

    if (response.ok) {
      const data = await response.json();
      return {
        isAdmin: data.is_admin === true,
        profile: {
          uid: currentUser.uid,
          email: data.email || currentUser.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          linkedInURL: data.linkedInURL || undefined,
          hasBeenPromptedForLinkedIn: data.hasBeenPromptedForLinkedIn || false,
          organization: data.organization || undefined,
        },
        accessState: 'allowed',
        accessMessage: '',
      };
    }

    let detailMessage = 'Your account is awaiting admin enablement.';
    let errorCode = '';
    try {
      const payload = await response.json();
      detailMessage =
        payload?.detail?.message ||
        payload?.message ||
        payload?.detail ||
        detailMessage;
      errorCode = String(payload?.detail?.code || payload?.code || '').toUpperCase();
    } catch {
      // keep defaults
    }

    const state: AccessState =
      errorCode === 'USER_DISABLED_BY_ADMIN' ? 'disabled' : 'pending_enable';
    const message =
      state === 'disabled'
        ? detailMessage || 'Your account is disabled. Contact an admin.'
        : detailMessage;

    return {
      isAdmin: false,
      profile: {},
      accessState: state,
      accessMessage: message,
    };
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (!firebaseUser) {
        setUserProfile(null);
        setIsAdmin(false);
        setAccessState('unknown');
        setAccessMessage('');
        setLoading(false);
        return;
      }

      // --- Try session cache first ---
      const cached = readCache(firebaseUser.uid);
      if (cached) {
        // Render immediately from cache — no spinner
        setAccessState(cached.accessState);
        setAccessMessage('');
        setIsAdmin(cached.isAdmin);
        setUserProfile({
          ...cached.profile,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as UserProfile);
        setLoading(false);

        // Re-verify in background — update state silently if anything changed
        checkBackendAccess(firebaseUser).then((result) => {
          if (!result) return; // backend unreachable — keep cached state
          setAccessState(result.accessState);
          setAccessMessage(result.accessMessage);
          setIsAdmin(result.isAdmin);
          if (result.accessState === 'allowed' && result.profile.uid) {
            const freshProfile: UserProfile = {
              uid: firebaseUser.uid,
              email: result.profile.email || firebaseUser.email || '',
              firstName: result.profile.firstName || '',
              lastName: result.profile.lastName || '',
              linkedInURL: result.profile.linkedInURL,
              hasBeenPromptedForLinkedIn: result.profile.hasBeenPromptedForLinkedIn || false,
              organization: result.profile.organization,
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            setUserProfile(freshProfile);
            // Refresh cache with latest values
            writeCache(firebaseUser.uid, {
              accessState: result.accessState,
              isAdmin: result.isAdmin,
              profile: {
                uid: firebaseUser.uid,
                email: freshProfile.email,
                firstName: freshProfile.firstName,
                lastName: freshProfile.lastName,
                linkedInURL: freshProfile.linkedInURL,
                hasBeenPromptedForLinkedIn: freshProfile.hasBeenPromptedForLinkedIn,
                organization: freshProfile.organization,
              },
              cachedAt: Date.now(),
            });
          } else if (result.accessState !== 'allowed') {
            // Account disabled or pending — clear cache and show gate
            clearCache(firebaseUser.uid);
          }
        }).catch(() => {
          // Background verification failed — keep cached state, will retry next load
        });
        return;
      }

      // --- No cache — full auth flow ---
      setLoading(true);
      setAccessState('unknown');
      setAccessMessage('');

      try {
        const result = await checkBackendAccess(firebaseUser);

        if (!result) {
          // Backend unreachable
          setAccessState('pending_enable');
          setAccessMessage('Backend unreachable. Please try again.');
          setLoading(false);
          return;
        }

        setAccessState(result.accessState);
        setAccessMessage(result.accessMessage);
        setIsAdmin(result.isAdmin);

        if (result.accessState === 'allowed' && result.profile.uid) {
          const profile: UserProfile = {
            uid: firebaseUser.uid,
            email: result.profile.email || firebaseUser.email || '',
            firstName: result.profile.firstName || firebaseUser.displayName?.split(' ')[0] || '',
            lastName: result.profile.lastName || firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
            linkedInURL: result.profile.linkedInURL,
            hasBeenPromptedForLinkedIn: result.profile.hasBeenPromptedForLinkedIn || false,
            organization: result.profile.organization,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          setUserProfile(profile);

          // Write to session cache for next page load
          writeCache(firebaseUser.uid, {
            accessState: 'allowed',
            isAdmin: result.isAdmin,
            profile: {
              uid: firebaseUser.uid,
              email: profile.email,
              firstName: profile.firstName,
              lastName: profile.lastName,
              linkedInURL: profile.linkedInURL,
              hasBeenPromptedForLinkedIn: profile.hasBeenPromptedForLinkedIn,
              organization: profile.organization,
            },
            cachedAt: Date.now(),
          });
        }
      } catch (err) {
        console.error('Auth state error:', err);
        setAccessState('pending_enable');
        setAccessMessage('Something went wrong. Please try signing in again.');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear session cache on explicit sign-out
      if (user) clearCache(user.uid);
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const retryAuth = async () => {
    if (!user) return;
    setLoading(true);
    setAccessMessage('');
    try {
      const result = await checkBackendAccess(user);
      if (!result) {
        setAccessState('pending_enable');
        setAccessMessage('Backend unreachable. Please try again.');
        return;
      }
      setAccessState(result.accessState);
      setAccessMessage(result.accessMessage);
      setIsAdmin(result.isAdmin);
      if (result.accessState === 'allowed' && result.profile.uid) {
        const profile: UserProfile = {
          uid: user.uid,
          email: result.profile.email || user.email || '',
          firstName: result.profile.firstName || user.displayName?.split(' ')[0] || '',
          lastName: result.profile.lastName || user.displayName?.split(' ').slice(1).join(' ') || '',
          linkedInURL: result.profile.linkedInURL,
          hasBeenPromptedForLinkedIn: result.profile.hasBeenPromptedForLinkedIn || false,
          organization: result.profile.organization,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setUserProfile(profile);
        writeCache(user.uid, {
          accessState: 'allowed',
          isAdmin: result.isAdmin,
          profile: {
            uid: user.uid,
            email: profile.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            linkedInURL: profile.linkedInURL,
            hasBeenPromptedForLinkedIn: profile.hasBeenPromptedForLinkedIn,
            organization: profile.organization,
          },
          cachedAt: Date.now(),
        });
      }
    } catch {
      setAccessState('pending_enable');
      setAccessMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('No user logged in');
    if (userProfile) {
      const updated: UserProfile = { ...userProfile, ...updates, updatedAt: new Date() };
      setUserProfile(updated);
      // Refresh cache with updated profile
      writeCache(user.uid, {
        accessState,
        isAdmin,
        profile: {
          uid: updated.uid,
          email: updated.email,
          firstName: updated.firstName,
          lastName: updated.lastName,
          linkedInURL: updated.linkedInURL,
          hasBeenPromptedForLinkedIn: updated.hasBeenPromptedForLinkedIn,
          organization: updated.organization,
        },
        cachedAt: Date.now(),
      });
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    accessState,
    accessMessage,
    loading,
    isAdmin,
    signInWithGoogle,
    signOut,
    updateUserProfile,
    retryAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
